export default {
  en: {
    title: 'Reporting',
    menu: {
      compliance: 'Compliance',
      activity: 'Activity',
      performance: 'Performance',
      risk: 'Risk',
    },
    card: {
      error: 'An error occurred while fetching this metric',
      retry: 'Retry',
    },
    averageCompliance: {
      title: 'Average compliance',
      description: 'Average compliance of all suppliers with complete assessments.',
      noChangeDays: 'No change in the past 1 day | No change in the past {n} days',
      changeDays: 'in the past 1 day | in the past {n} days',
      noChange: 'No change in the {0}',
      change: 'in the {0}',
      suppliers:
        '<b>{n}</b> supplier with a completed assessment, out of {total} total. | <b>{n}</b> suppliers with completed assessments, out of {total} total.',
    },
    complianceBreakdown: {
      title: 'Compliance breakdown',
      description: 'The number of suppliers in different compliance intervals.',
      xLabel: 'Compliance percentage',
      yLabel: 'Number of suppliers',
    },
    complianceByCriticality: {
      title: 'Compliance by criticality',
      description: 'Average compliance of suppliers with each criticality tag.',
    },
    complianceByConfidentiality: {
      title: 'Compliance by confidentiality',
      description: 'Average compliance of suppliers with each confidentiality tag.',
    },
    complianceByPersonalData: {
      title: 'Compliance by personal data',
      description: 'Average compliance of suppliers with each personal data tag.',
    },
    complianceByDomain: {
      title: 'Compliance by domain',
      description: 'Average compliance per domain across all supplier answers.',
      nonCompliant: '{count} non-compliant',
    },
    leastCompliantSuppliers: {
      title: 'Least compliant suppliers',
      description: 'Your least compliant suppliers in ascending order.',
    },
    complianceByLabel: {
      title: 'Compliance by labels',
      description: 'Average compliance of suppliers with each custom label.',
    },
    barChart: {
      suppliers: '{count} suppliers | {count} supplier | {count} suppliers',
    },
    timeMetric: {
      bucketLabel: '{0}-{1} days',
      xLabel: 'Time',
      yLabel: 'Number of suppliers',
      format: {
        noDurations: 'None',
        daysHours: '{0}d {1}h',
        hoursMinutes: '{0}h {1}m',
      },
    },
    page: {
      period: 'Period: ',
      allTime: 'All time',
      noSuppliersTitle: 'No suppliers have completed their assessment yet.',
      noSuppliersSubtitle: 'Their compliance will appear here when they do.',
      filters: {
        lastMonth: 'Last 30 days',
        lastWeek: 'Last 7 days',
        lastQuarter: 'Last quarter',
      },
    },
    compliance: {
      export: {
        button: 'Export CSV',
        pdfButton: 'Export PDF',
        failed: 'Export failed. Please try again. .',
        from: 'From',
        to: 'To',
        metric: 'Metric',
        value: 'Value',
        fileName: 'Risk_Ledger_Compliance_{from}_to_{to}.csv',
        dateFormat: 'yyyy-MM-dd',
        avgComp: 'Average compliance of all suppliers with completed assessment',
        compAss: 'Completed assessments',
        newCompAss: 'New completed assessments for time period',
        totNumSuppliers: 'Total number of suppliers',
        name: 'Name',
        compliance: 'Compliance',
        delta: 'Change for time period',
        supplierCount: 'Supplier count',
        breakdown: 'Compliance percentage for suppliers with completed assessments',
      },
    },
    activity: {
      newSuppliers: 'New supplier | New suppliers',
      newSuppliersHelp: 'Total number of new suppliers connected to.',
      assessmentsCompleted: 'Completed assessment | Completed assessments',
      assessmentsCompletedHelp: 'Total number of initial assessments completed by suppliers.',
      sentInvitesAndCR: 'Sent invites & connection requests',
      sentInvitesAndCRHelp: 'The sum of invites and connection requests sent to suppliers.',
      acceptedInvitesAndCR: 'Accepted invites & connection requests',
      acceptedInvitesAndCRHelp: 'The sum of invites and connection requests accepted by suppliers.',
      suppliersApproved: 'Approved supplier | Approved suppliers',
      suppliersApprovedHelp: 'Total number of approvals given to suppliers.',
      suppliersRejected: 'Rejected supplier | Rejected suppliers',
      suppliersRejectedHelp: 'Total number of rejections given to suppliers.',
      invitesSent: 'Invites sent',
      invitesAccepted: 'Invites accepted',
      crSent: 'Connection requests sent',
      crAccepted: 'Connection requests accepted',
      dateFormat: 'MMMM d, YYYY',
      export: {
        button: 'Export CSV',
        pdfButton: 'Export PDF',
        failed: 'Export failed. Please try again. .',
        from: 'From',
        to: 'To',
        metric: 'Metric',
        value: 'Value',
        fileName: 'Risk_Ledger_Activity_{from}_to_{to}.csv',
        newSuppliers: 'New suppliers',
        assessmentsCompleted: 'Completed assessments',
        invitesSent: 'Invites sent',
        invitesAccepted: 'Invites accepted',
        connectionRequestsSent: 'Connection requests sent',
        outgoingConnectionRequestsAccepted: 'Outgoing connection requests accepted',
        suppliersApproved: 'Suppliers approved',
        suppliersRejected: 'Suppliers rejected',
        dateFormat: 'yyyy-MM-dd',
      },
    },
    perf: {
      min: 'Minimum',
      max: 'Maximum',
      mean: 'Mean',
      median: 'Median',
      requestToApproval: '{aggregate} time from invite/request to approval',
      requestToApprovalHelp:
        'The aggregated length of time between inviting or requesting a supplier and the first approval or rejection of the supplier. Includes all suppliers approved or rejected within the time period.',
      connectionRequestToApproval: 'Connection request to approval',
      inviteToApproval: 'Invite sent to approval',
      requestToAssessmentComplete: '{aggregate} time from invite/request to assessment complete',
      requestToAssessmentCompleteHelp:
        'The aggregated length of time between inviting or requesting a supplier and the completion of their initial assessment. Includes all suppliers that completed their assessment within the time period.',
      connectionRequestToAssessmentComplete: 'Connection request to assessment complete',
      inviteToAssessmentComplete: 'Invite sent to assessment complete',
      requestToConnected: '{aggregate} time from invite/request to connected',
      requestToConnectedHelp:
        'The aggregated length of time between inviting or requesting a supplier and the acceptance of the invite or request. Includes all suppliers that were connected to within the time period.',
      connectionRequestToConnected: 'Connection request to connected',
      inviteToConnected: 'Invite sent to connected',
      assessmentCompleteToApproval: '{aggregate} time from assessment complete to approval',
      assessmentCompleteToApprovalHelp:
        "The aggregated length of time between the completion of a supplier's initial assessment and the first approval or rejection of the supplier. Includes all suppliers approved or rejected within the time period.",
      export: {
        button: 'Export CSV',
        pdfButton: 'Export PDF',
        failed: 'Export failed. Please try again. .',
        from: 'From',
        to: 'To',
        metric: 'Metric',
        mean: 'Mean minutes',
        median: 'Median minutes',
        min: 'Minimum minutes',
        max: 'Maximum minutes',
        dateFormat: 'yyyy-MM-dd',
        fileName: 'Risk_Ledger_Performance_{from}_to_{to}.csv',
        requestToApproval: 'Invite/request to approval',
        connectionRequestToApproval: 'Connection request to approval',
        inviteToApproval: 'Invite to approval',
        requestToAssessmentComplete: 'Invite/request to assessment complete',
        connectionRequestToAssessmentComplete: 'Connection request to assessment complete',
        inviteToAssessmentComplete: 'Invite to assessment complete',
        requestToConnected: 'Invite/request to connected',
        connectionRequestToConnected: 'Connection request to connected',
        inviteToConnected: 'Invite to connected',
        assessmentCompleteToApproval: 'Assessment complete to approval',
      },
    },
    risk: {
      export: {
        button: 'Export CSV',
        pdfButton: 'Export PDF',
        failed: 'Export failed. Please try again. .',
        from: 'From',
        to: 'To',
        metric: 'Metric',
        value: 'Value',
        fileName: 'Risk_Ledger_Risk_{from}_to_{to}.csv',
        dateFormat: 'yyyy-MM-dd',
      },
      openRisks: {
        title: 'Open risks',
        description: 'The number of risks that are currently open.',
      },
      closedRisks: {
        title: 'Closed risks',
        description: 'The number of risks that are currently closed.',
      },
      assignedRisks: {
        title: 'Assigned risks',
        description: 'The number of risks that are currently assigned to users.',
      },
      riskMatrix: {
        title: 'Risk matrix',
        description: 'These are the number of open risks by impact and likelihood scores.',
      },
      viewAllRisks: 'View all risks',
      risksBySuppliers: {
        title: 'Top ten suppliers by number of open risks',
        description: 'These are the top ten suppliers with the highest number of open risks.',
      },
      viewAllSuppliers: 'View all suppliers',
      risks: '{n} risk | {n} risks',
      risksByControls: {
        title: 'Top ten controls by number of open risks',
        description:
          'These are the top ten controls among all your suppliers based on the number of open risks.',
        unknownControl: 'Unknown control',
      },
      risksByCriticality: {
        title: 'Open risks by supplier criticality',
        description: 'The number of open risks of suppliers with each criticality tag.',
      },
      risksByConfidentiality: {
        title: 'Open risks by supplier confidentiality',
        description: 'The number of open risks of suppliers with each confidentiality tag.',
      },
      risksByPII: {
        title: 'Open risks by supplier personal data',
        description: 'The number of open risks of suppliers with each personal data tag.',
      },
      risksByLabel: {
        title: 'Open risks by supplier labels',
        description: 'The number of open risks of suppliers with each custom label.',
      },
    },
    timePeriodSelector: {
      dateFormat: 'MMMM d, yyyy',
      timePeriod: '{0} - {1}',
      allTime: 'All time',
      lastQuarter: 'Past 90 days',
      lastMonth: 'Past 30 days',
      lastWeek: 'Past 7 days',
      custom: 'Custom',
      apply: 'Apply',
      cancel: 'Cancel',
    },
  },
};
